import { useContext, useEffect, useState } from 'react';
import { ContentContext } from './contexts';
import { getPerSlideWltpCompliance } from './wltpCompliance';
import type { Logger } from '@feature-hub/core';

export interface UseWltpComplianceGateArgs {
  isMarketComplianceEnabled?: boolean;
  initialCompliance?: boolean[];
  logger?: Pick<Logger, 'warn' | 'error'>;
}

export const useWltpComplianceGate = ({
  isMarketComplianceEnabled,
  initialCompliance,
  logger,
}: UseWltpComplianceGateArgs): boolean[] | undefined => {
  const context = useContext(ContentContext);
  const content = context?.content;
  const { localeService, vueFormatterService } = context ?? {};

  const isGateActive = Boolean(isMarketComplianceEnabled);
  const isServerResultAvailable = initialCompliance !== undefined;

  const [clientCompliance, setClientCompliance] = useState<boolean[] | undefined>(undefined);

  useEffect(() => {
    const isClientCheckNeeded = isGateActive && !isServerResultAvailable;
    if (!isClientCheckNeeded) return undefined;
    if (!content?.gallery || !vueFormatterService || !localeService) return undefined;

    const controller = new AbortController();
    const runCheck = async () => {
      const perSlideCompliance = await getPerSlideWltpCompliance(
        content.gallery,
        vueFormatterService,
        localeService as Parameters<typeof getPerSlideWltpCompliance>[2],
        logger,
      );
      if (!controller.signal.aborted) {
        setClientCompliance(perSlideCompliance);
      }
    };
    void runCheck();
    return () => {
      controller.abort();
    };
  }, [isGateActive, isServerResultAvailable, content, localeService, vueFormatterService, logger]);

  if (!isGateActive) return undefined;

  const compliance = isServerResultAvailable ? initialCompliance : clientCompliance;
  return compliance;
};
